<script setup>
import { Chart } from 'chart.js';
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';

import { ensureChartRegistered } from './chartTheme.js';

defineOptions({ name: 'AnalyticsChart' });
const props = defineProps({
  type: { type: String, required: true },
  labels: { type: Array, default: () => [] },
  datasets: { type: Array, default: () => [] },
  ariaLabel: { type: String, required: true },
  percentage: { type: Boolean, default: false },
  percentageTotal: { type: Number, default: null },
  compact: { type: Boolean, default: false },
  centerValue: { type: String, default: '' },
  centerLabel: { type: String, default: '' },
  indexAxis: { type: String, default: 'y' }
});
const canvas = ref(null);
let chart = null;
let themeObserver = null;

function css(name, fallback) {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim() || fallback;
}
function resolveColor(value) {
  if (typeof value !== 'string') return value;
  const match = value.match(/^var\((--[^)]+)\)$/);
  return match ? css(match[1], '#2563eb') : value;
}
function normalizeDataValue(value) {
  if (typeof value !== 'number' && typeof value !== 'string') return null;
  if (typeof value === 'string' && value.trim() === '') return null;
  // Preserve existing numeric-string callers (e.g. SQL counters).
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}
function isolatedPointRadius(context) {
  const data = context.dataset?.data;
  const index = context.dataIndex;
  if (!Number.isFinite(data?.[index])) return 0;
  return !Number.isFinite(data[index - 1]) && !Number.isFinite(data[index + 1]) ? 3 : 0;
}
function render() {
  chart?.destroy();
  if (!canvas.value) return;
  const context = canvas.value.getContext?.('2d');
  if (!context) return;
  ensureChartRegistered();
  const palette = ['--chart-1', '--chart-2', '--chart-3', '--chart-4', '--chart-5', '--chart-6'];
  const normalized = props.datasets.map((set) => ({
    ...set,
    // Missing or invalid samples are gaps, never zero-valued measurements.
    data: Array.isArray(set.data) ? set.data.map(normalizeDataValue) : [],
  }));
  const displayedTotal = normalized.reduce(
    (sum, set) => sum + set.data.reduce((subtotal, value) => subtotal + (Number.isFinite(value) ? value : 0), 0),
    0,
  );
  const total = props.percentageTotal ?? displayedTotal;
  chart = new Chart(context, {
    type: props.type,
    data: {
      // Chart.js instrumenta sus arrays; no debe modificar arrays reactivos de Vue.
      labels: [...props.labels],
      datasets: normalized.map((set, index) => ({
        ...set,
        borderColor: resolveColor(set.borderColor) || css(palette[index % palette.length], '#2563eb'),
        backgroundColor: set.backgroundColor
          ? (Array.isArray(set.backgroundColor) ? set.backgroundColor.map(resolveColor) : resolveColor(set.backgroundColor))
          : (props.type === 'doughnut'
          ? props.labels.map((_, i) => css(palette[i % palette.length], '#2563eb'))
          : css(palette[index % palette.length], '#2563eb')),
        borderWidth: props.type === 'line' ? 2 : 0,
        borderRadius: props.type === 'bar' ? 5 : 0,
        maxBarThickness: 20,
        tension: 0.25,
        // A line must not bridge missing observations. Isolated real samples
        // keep a dot so sparse telemetry remains visible without suggesting a trend.
        spanGaps: false,
        pointRadius: props.type === 'line' ? isolatedPointRadius : 0,
        fill: false
      }))
    },
    options: {
      animation: false,
      responsive: true,
      maintainAspectRatio: false,
      // Chart.js 4.5's delayed resize update survives destroy(). Theme/data
      // changes recreate this chart, so that timer could update a released
      // canvas. Keep the built-in RAF resize throttling without another timer.
      resizeDelay: 0,
      cutout: '72%',
      indexAxis: props.type === 'bar' ? props.indexAxis : 'x',
      plugins: {
        tooltip: {
          backgroundColor: css('--bg-inverse', '#18181b'),
          titleColor: css('--fg-inverse', '#fff'),
          bodyColor: css('--fg-inverse', '#fff'),
          callbacks: {
            title(contexts) {
              const context = contexts[0];
              return context?.dataset?.tooltipLabels?.[context.dataIndex]
                ?? context?.label
                ?? '';
            },
            label(context) {
              const name = context.dataset.label ? `${context.dataset.label}: ` : '';
              const value = Number(context.raw || 0);
              const pct = props.percentage && total > 0 ? ` (${((value / total) * 100).toFixed(1)}%)` : '';
              const ownTimestamp = context.dataset.tooltipLabels?.[context.dataIndex];
              const timestamp = ownTimestamp ? `${ownTimestamp} · ` : '';
              return `${timestamp}${name}${value.toLocaleString()}${pct}`;
            }
          }
        }
      },
      scales: props.type === 'doughnut' ? undefined : {
        x: { display: !props.compact, beginAtZero: props.type === 'bar' && props.indexAxis === 'y', ticks: { color: css('--fg-secondary', '#666'), maxTicksLimit: 6, maxRotation: 0 }, grid: { display: false }, border: { display: false } },
        y: { display: !props.compact, ticks: { color: css('--fg-secondary', '#666'), maxTicksLimit: 5 }, grid: { display: props.type !== 'bar' || props.indexAxis !== 'y', color: css('--border-default', '#ddd') }, border: { display: false }, beginAtZero: true }
      }
    }
  });
}

onMounted(() => {
  render();
  themeObserver = new MutationObserver(render);
  themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['class', 'data-theme', 'data-accent'] });
});
watch(() => [props.type, props.labels, props.datasets, props.percentage, props.percentageTotal, props.indexAxis, props.compact], async () => { await nextTick(); render(); }, { deep: true });
onBeforeUnmount(() => {
  themeObserver?.disconnect();
  chart?.destroy();
});
</script>

<template>
  <div
    class="analytics-chart"
    :class="{ 'analytics-chart--doughnut': type === 'doughnut', 'analytics-chart--compact': compact }"
  >
    <canvas
      ref="canvas"
      role="img"
      :aria-label="ariaLabel"
    />
    <div
      v-if="type === 'doughnut' && centerValue"
      class="analytics-chart__center"
      aria-hidden="true"
    >
      <strong>{{ centerValue }}</strong><span>{{ centerLabel }}</span>
    </div>
  </div>
</template>

<style scoped>
.analytics-chart {
  position: relative;
  height: 18rem;
  min-width: 0;
  width: 100%;
}

.analytics-chart--doughnut {
  height: 12rem;
}

.analytics-chart--compact {
  height: 3rem;
}

.analytics-chart__center {
  position: absolute;
  inset: 25%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  text-align: center;
}

.analytics-chart__center strong {
  font-size: var(--text-2xl);
  color: var(--fg-primary);
  font-variant-numeric: tabular-nums;
}

.analytics-chart__center span {
  font-size: var(--text-xs);
  color: var(--fg-secondary);
}
</style>
